/**
 * Thin request layer for the Integrations module. Endpoints (backend contracts):
 *   /me/integrations/credentials*   thirdPartyCredential.routes.ts   (Lead Advisor)
 *   /admin/platform-credentials*    platformServiceCredential.routes.ts (platform_admin)
 *   /projects/:id/contract-signing* contractSigning.routes.ts
 * Tenant/project scoping is applied server-side from the token + path (Commandment III);
 * nothing here is a client-side filter. Secrets are write-only: no response carries one.
 */
import { apiConfig } from '../authConfig';
import { ApiError, apiErrorFromResponse, toApiError } from './apiError';

export { ApiError };

/**
 * @param {() => Promise<string>} getToken
 * @returns {Promise<any>} the envelope's `data` payload
 */
export async function apiRequest(getToken, path, { method = 'GET', body, formData, headers = {} } = {}) {
    let token;
    try {
        token = await getToken();
    } catch (err) {
        throw toApiError(err);
    }
    const init = { method, headers: { Authorization: `Bearer ${token}`, ...headers } };
    if (formData) {
        init.body = formData; // browser sets the multipart boundary
    } else if (body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }
    let res;
    try {
        res = await fetch(`${apiConfig.baseUrl}/api/v1${path}`, init);
    } catch (err) {
        throw toApiError(err);
    }
    const contentType = res.headers.get('content-type') || '';
    const json = contentType.includes('json') ? await res.json().catch(() => null) : null;
    if (!res.ok || !json || json.success === false) {
        throw apiErrorFromResponse(res, json);
    }
    return json.data;
}

// ── Lead Advisor: own third-party credentials ───────────────────────────────
export const listMyCredentials = (getToken) =>
    apiRequest(getToken, '/me/integrations/credentials').then((d) => d?.credentials ?? []);

export const saveMyCredential = (getToken, { provider, secrets, displayLabel }) =>
    apiRequest(getToken, '/me/integrations/credentials', {
        method: 'POST',
        body: { provider, secrets, ...(displayLabel ? { displayLabel } : {}) },
    });

export const deleteMyCredential = (getToken, id) =>
    apiRequest(getToken, `/me/integrations/credentials/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ── platform_admin: platform service keys ───────────────────────────────────
export const listPlatformCredentials = (getToken) =>
    apiRequest(getToken, '/admin/platform-credentials').then((d) => d?.credentials ?? []);

export const savePlatformCredential = (getToken, { provider, secretValue, displayLabel }) =>
    apiRequest(getToken, '/admin/platform-credentials', {
        method: 'POST',
        body: { provider, secretValue, ...(displayLabel ? { displayLabel } : {}) },
    });

export const deletePlatformCredential = (getToken, provider) =>
    apiRequest(getToken, `/admin/platform-credentials/${encodeURIComponent(provider)}`, { method: 'DELETE' });

// ── Provider catalogue + verify + enrichment (backend e912b97) ──────────────
/** Only providers with a REAL adapter behind them, each with its credential form fields. */
export const listProviders = (getToken) =>
    apiRequest(getToken, '/me/integrations/providers').then((d) => d?.providers ?? []);

export const verifyMyCredential = (getToken, id) =>
    apiRequest(getToken, `/me/integrations/credentials/${encodeURIComponent(id)}/verify`, { method: 'POST' });

/** Suggestions only — the server never writes them anywhere. */
export const enrichCompany = (getToken, { provider, domain, name }) =>
    apiRequest(getToken, '/me/integrations/enrichment/company', {
        method: 'POST',
        body: { provider, ...(domain ? { domain } : {}), ...(name ? { name } : {}) },
    });

// ── platform_admin: configure Lead-Advisor-level integrations ON BEHALF of a named advisor ──
// Routes: /admin/lead-advisors/:userId/integrations*. Same write-only contract: responses are metadata
// ({provider,status,maskedPreview,label,updatedAt}); the server never returns a secret.
const asList = (d, ...keys) => (Array.isArray(d) ? d : keys.map((k) => d?.[k]).find(Array.isArray) ?? []);

/** Roster used by the Lead Advisors screen: `GET /lead-advisors` -> `{ leadAdvisors: [{id,name,email,...}] }`. */
export const listLeadAdvisorRoster = (getToken) =>
    apiRequest(getToken, '/lead-advisors').then((d) => asList(d, 'leadAdvisors'));

const onBehalfPath = (userId) => `/admin/lead-advisors/${encodeURIComponent(userId)}/integrations`;

export const listAdvisorIntegrations = (getToken, userId) =>
    apiRequest(getToken, onBehalfPath(userId)).then((d) => asList(d, 'integrations', 'credentials'));

export const saveAdvisorIntegration = (getToken, userId, provider, { secrets, label }) =>
    apiRequest(getToken, `${onBehalfPath(userId)}/credentials/${encodeURIComponent(provider)}`, {
        method: 'PUT',
        body: { secrets, ...(label ? { label } : {}) },
    });

export const deleteAdvisorIntegration = (getToken, userId, provider) =>
    apiRequest(getToken, `${onBehalfPath(userId)}/credentials/${encodeURIComponent(provider)}`, { method: 'DELETE' });

// ── Open API keys (`/me/api-keys*`): Lead Advisor manages; platform_admin lists/revokes the tenant's ──
export const listApiKeys = (getToken) =>
    apiRequest(getToken, '/me/api-keys').then((d) => asList(d, 'keys', 'apiKeys'));

export const listApiKeyScopes = (getToken) =>
    apiRequest(getToken, '/me/api-keys/scopes').then((d) => asList(d, 'scopes'));

/** Resolves `{ key, secret }`; `secret` is returned exactly once by the server. */
export const createApiKey = (getToken, { name, scopes, expiresInDays, ipAllowlist, rateLimitPerMinute }) =>
    apiRequest(getToken, '/me/api-keys', {
        method: 'POST',
        body: {
            name,
            scopes,
            ...(expiresInDays ? { expiresInDays } : {}),
            ...(ipAllowlist && ipAllowlist.length ? { ipAllowlist } : {}),
            ...(rateLimitPerMinute ? { rateLimitPerMinute } : {}),
        },
    });

export const rotateApiKey = (getToken, id, { overlapHours } = {}) =>
    apiRequest(getToken, `/me/api-keys/${encodeURIComponent(id)}/rotate`, {
        method: 'POST',
        body: overlapHours !== undefined ? { overlapHours } : {},
    });

export const revokeApiKey = (getToken, id) =>
    apiRequest(getToken, `/me/api-keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
